import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { CalendarConstraintHint, CalendarInputCommonProps, CalendarInterval, CalendarPeriodName } from './types';
import { MINUTE_RESOLUTION } from './resolution';
import { useProviderConfig } from '../../provider';
import { getDatePickerLocale } from '../DatePickerLocale';
import { getPeriods } from './period';
import { subMilliseconds } from 'date-fns';
import {
  isInstantWithinItem,
  getNewCursorItem,
  applyItemIntervalWithFallback,
  resolveIntervalWithOffsets,
  isSelectionFromAvailableWithHint,
  isSelectionToAvailableWithHint,
} from './utils';
import { time_side_input_button_css, time_side_input_container_css, time_side_input_list_css } from './styles';
import { Composite, CompositeItem } from '@floating-ui/react';

export interface TimeSideInputProps extends CalendarInputCommonProps {
  /**
   * 최소 선택 단위 (hour/minute) 을 지정합니다.
   */
  period?: CalendarPeriodName;
}

export const TimeSideInput: React.FC<TimeSideInputProps> = ({
  resolution = MINUTE_RESOLUTION,
  period = 'hour',
  constraint,
  items,
  cursor,
  target,
  disabled,
  onCursorChange,
}) => {
  const { locale } = useProviderConfig();
  const datePickerLocale = getDatePickerLocale(locale?.lang);
  const currentTarget = target ?? cursor?.focus ?? 'from';
  const isFrom = currentTarget === 'from' || currentTarget === 'all';
  const currentDate = useMemo(
    () => (isFrom ? cursor?.item?.from : cursor?.item?.to && subMilliseconds(cursor.item.to, 1)),
    [cursor, isFrom],
  );
  const {
    resolution: periodResolution,
    periods,
    keepOffset,
  } = useMemo(
    () => getPeriods(period, currentDate ?? new Date(), datePickerLocale),
    [period, currentDate, datePickerLocale],
  );

  const checkAvailable = useCallback(
    (date: Date): boolean => {
      if (disabled) {
        return false;
      }
      // 날짜 선택이 안 되어 있으면 선택 불가능
      if ((currentTarget === 'from' || currentTarget === 'all') && cursor?.item?.from == null) {
        return false;
      }
      if (currentTarget === 'to' && cursor?.item?.to == null) {
        return false;
      }
      const interval = periodResolution.resolve(date);
      if (currentTarget === 'to' && cursor?.item?.from != null) {
        const from = cursor.item.from;
        if ((cursor.toggled || target === 'to') && interval.to <= from) {
          return false;
        }
      }
      if (currentTarget === 'from' && cursor?.item?.to != null) {
        const to = cursor.item.to;
        if ((cursor.toggled || target === 'from') && interval.from >= to) {
          return false;
        }
      }
      return true;
    },
    [currentTarget, cursor, disabled, periodResolution, target],
  );

  const checkConstraintAvailable = useCallback(
    (date: Date): { available: boolean; hint: CalendarConstraintHint | null } => {
      const interval = periodResolution.resolve(date);

      if (currentTarget === 'to') {
        return isSelectionToAvailableWithHint(constraint, { items }, cursor?.item ?? {}, interval);
      }

      return isSelectionFromAvailableWithHint(constraint, { items }, cursor?.item ?? {}, interval);
    },
    [constraint, currentTarget, cursor, items, periodResolution],
  );

  const modifiers = useMemo(() => {
    const withPeriodInterval = (fn: (pi: CalendarInterval) => boolean) => (date: Date) => {
      const pi = periodResolution.resolve(date);
      return fn(pi);
    };
    return {
      selecting: withPeriodInterval((di) => {
        if (isFrom && cursor?.item?.from && isInstantWithinItem(di, cursor.item.from)) return true;
        if (!isFrom && cursor?.item?.to && isInstantWithinItem(di, subMilliseconds(cursor.item.to, 1))) return true;
        return false;
      }),
    } as const;
  }, [isFrom, periodResolution, cursor]);

  const periodItems = useMemo(
    () =>
      periods
        .map((period) => {
          const date = period.interval.from;
          const selecting = modifiers.selecting(date);
          const available = checkAvailable(date);
          const constraintAvailable = checkConstraintAvailable(date);
          return {
            ...period,
            date,
            selecting,
            disabled: !(available && constraintAvailable?.available),
            hidden: constraintAvailable?.hint?.hideInterval,
          };
        })
        .filter((period) => !period.hidden),
    [checkAvailable, checkConstraintAvailable, modifiers, periods],
  );
  const activeIndex = useMemo(() => periodItems.findIndex((v) => v.selecting), [periodItems]);

  const handleSelect = useCallback(
    (index: number) => {
      const period = periodItems[index];
      if (period != null && !period.disabled) {
        const interval = resolution.resolve(period.date);
        const item = applyItemIntervalWithFallback(
          cursor?.item ?? getNewCursorItem(),
          constraint,
          { items },
          resolveIntervalWithOffsets(
            resolution,
            period.date,
            keepOffset ? cursor?.item?.from : undefined,
            keepOffset ? cursor?.item?.to : undefined,
          ),
          interval,
          currentTarget,
        ).item;

        onCursorChange({ ...cursor, item, focus: cursor?.focus });
      }
    },
    [periodItems, resolution, cursor, constraint, items, keepOffset, currentTarget, onCursorChange],
  );

  const handleMouseEnter = useCallback(
    (index: number) => {
      const period = periodItems[index];
      if (period == null || !cursor?.active) {
        return;
      }
      const { item } = applyItemIntervalWithFallback(
        cursor?.item ?? {},
        constraint,
        { items },
        resolveIntervalWithOffsets(
          resolution,
          period.date,
          keepOffset ? cursor?.item?.from : undefined,
          keepOffset ? cursor?.item?.to : undefined,
        ),
        resolution.resolve(period.date),
        currentTarget,
      );
      if (currentTarget === 'all') {
        onCursorChange({ ...cursor, fromHover: item?.from, toHover: item?.to });
        return;
      }
      if (currentTarget === 'from' || cursor?.focus == null) {
        onCursorChange({ ...cursor, fromHover: item?.from });
        return;
      }
      if (currentTarget === 'to' && cursor?.item?.from != null) {
        onCursorChange({ ...cursor, toHover: item?.to });
        return;
      }
    },
    [constraint, currentTarget, cursor, items, keepOffset, onCursorChange, periodItems, resolution],
  );

  const handleMouseLeave = useCallback(() => {
    if (cursor?.fromHover != null || cursor?.toHover != null) {
      onCursorChange({ ...cursor, fromHover: undefined, toHover: undefined });
    }
  }, [cursor, onCursorChange]);

  const listRef = useRef<HTMLDivElement | null>(null);
  const isFromRef = useRef<boolean | null>(null);
  useEffect(() => {
    const listEl = listRef.current;
    if (activeIndex !== -1 && listEl != null) {
      const childEl = listEl.children[activeIndex];
      if (childEl != null) {
        const shouldScrollSmoothly = isFromRef.current === isFrom;
        const listRect = listEl.getBoundingClientRect();
        const childRect = childEl.getBoundingClientRect();
        const nextScrollTop =
          listEl.scrollTop + (childRect.top - listRect.top) - (listEl.clientHeight - childRect.height) / 2;
        if (typeof listEl.scrollTo === 'function') {
          listEl.scrollTo({ top: nextScrollTop, behavior: shouldScrollSmoothly ? 'smooth' : 'auto' });
        } else {
          listEl.scrollTop = nextScrollTop;
        }
        isFromRef.current = isFrom;
      }
    }
  }, [activeIndex, isFrom]);

  return (
    <div css={time_side_input_container_css}>
      <Composite
        activeIndex={activeIndex}
        onNavigate={handleSelect}
        render={<div css={time_side_input_list_css} />}
        ref={listRef}
      >
        {periodItems.map((period, index) => (
          <CompositeItem
            key={index}
            render={
              <button
                type='button'
                className='pds-time-side-input-option'
                css={time_side_input_button_css}
                aria-selected={period.selecting}
                disabled={period.disabled}
                key={index}
                onClick={() => handleSelect(index)}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={() => handleMouseLeave()}
              >
                {period.name}
              </button>
            }
          />
        ))}
      </Composite>
    </div>
  );
};
